import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { withQuery } from '@/lib/qs';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type { Paginated } from '../types/common';
import type {
  CourierPickup,
  CourierPickupFilters,
  CreateCourierPickupPayload,
  PickupCutoff,
} from '../types/pickups';

type Wrapped<T> = { data: T };

export function useCourierPickups(filters: CourierPickupFilters) {
  const api = useApiClient();
  return useQuery<Paginated<CourierPickup>, Error>({
    queryKey: queryKeys.courierPickups.list(filters),
    queryFn: ({ signal }) => api.get(withQuery('/admin/courier-pickups', filters), signal),
    // New rows appear when the merchant creates one; the list also reflects async
    // status syncs, so keep it fresh on mount/focus.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/**
 * A single pickup. The backend refreshes its status live from InPost on this
 * request, so opening the detail view reflects the latest state.
 */
export function useCourierPickup(id: number | null) {
  const api = useApiClient();
  return useQuery<CourierPickup, Error>({
    queryKey: queryKeys.courierPickups.detail(id),
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<CourierPickup>>(
        `/admin/courier-pickups/${id}`,
        signal,
      );
      return response.data;
    },
    enabled: id !== null,
    staleTime: 0,
  });
}

export function useCreateCourierPickup() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CourierPickup, Error, CreateCourierPickupPayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<CourierPickup>>('/admin/courier-pickups', payload);
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.courierPickups.all });
    },
  });
}

export function useCancelCourierPickup() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CourierPickup, Error, number>({
    mutationFn: async (id) => {
      const response = await api.post<Wrapped<CourierPickup>>(
        `/admin/courier-pickups/${id}/cancel`,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.courierPickups.all });
    },
  });
}

/**
 * Latest local hour a pickup can be ordered for a location's postal code, used to
 * constrain the create form. `null` location disables the query.
 */
export function usePickupCutoff(locationId: number | null) {
  const api = useApiClient();
  return useQuery<PickupCutoff | null, Error>({
    queryKey: queryKeys.courierPickups.cutoff(locationId),
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<{ cutoff: PickupCutoff | null }>>(
        withQuery('/admin/courier-pickups/cutoff-time', { pickup_location_id: locationId }),
        signal,
      );
      return response.data.cutoff;
    },
    enabled: locationId !== null,
    staleTime: 5 * 60 * 1000,
  });
}
